import t from 'react-translate';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { omit } from 'underscore';

// redux
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';

import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { lightOrange } from 'athena/styles/colors';
import { config } from '../../../../../../config/pendo.config.json';
import RolePlayContextSetting from './role-play-context-setting';
import CharacterSetting from './ai-character-setting';
import LearnerObjectiveSetting from './ai-learner-objective-setting';
import { LectureContentQualityStatus } from '..';

type SetUpInstructionProps = {
  setStep?: (step: number) => void;
  isUpdate?: boolean;
  onSubmit?: (...args: any[]) => Promise<any>;
  onCancel?: () => void;
  lectureContentQualityStatus?: LectureContentQualityStatus;
};

const styles = css`
  .btn-container {
    gap: ${halfSpacing}px;
  }
  .lecture-content-warning {
    background-color: ${lightOrange};
  }
`;

enum SubmitType {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
}

const SetUpInstruction = (props: SetUpInstructionProps) => {
  const {
    isUpdate = false,
    onSubmit,
    onCancel,
    lectureContentQualityStatus,
  } = props || {};
  const dispatch = useDispatch();
  const { getValues, formState } = useFormContext();

  const [submitting, setSubmitting] = useState(false);
  const { isValid, isDirty } = formState;
  const isSubmitDisabled = !isValid || !isDirty;

  const handleCancel = () => {
    if (isDirty) {
      dispatch(openConfirmationDialog({
        onConfirm: () => onCancel(),
        cancelText: t.FORM.CANCEL(),
        confirmText: t.FORM.YES_SURE(),
        title: t.FORM.UNSAVED_CHANGES.CLOSE_WINDOW.TITLE(),
        bodyText: t.FORM.UNSAVED_CHANGES.CLOSE_WINDOW.DESCRIPTION(),
      }));
    } else {
      onCancel();
    }
  };

  const getPayload = (formData, submitType) => {
    const normalizeText = (text) => (typeof text === 'string' ? text.trim() : text);
    const normalizeFormTextFields = (data) => ({
      ...data,
      title: normalizeText(data?.title),
      description: normalizeText(data?.description),
      learnerRole: normalizeText(data?.learnerRole),
      aiCharacterRole: normalizeText(data?.aiCharacterRole),
      aiCharacterName: normalizeText(data?.aiCharacterName),
      context: normalizeText(data?.context),
    });

    switch (submitType) {
      case SubmitType.CREATE:
      {
        const cleanedFormData = omit(normalizeFormTextFields(formData),
          [
            'requiredAiLearnerGoal',
            'aiCharacterObjections',
            'aiLearnerGoals',
          ]);
        const aiCharacterObjections = [
          ...formData.aiCharacterObjections
            .map((item) => normalizeText(item?.text))
            .filter((text) => text !== ''),
        ];

        const aiLearnerGoals = [
          normalizeText(formData.requiredAiLearnerGoal.text),
          ...formData.aiLearnerGoals
            .map((item) => normalizeText(item?.text))
            .filter((text) => text !== ''),
        ];
        return {
          ...cleanedFormData,
          aiCharacterObjections,
          aiLearnerGoals,
        };
      }
      case SubmitType.UPDATE:
      {
        // Omit form-only fields and normalize arrays to API's expected shape
        const cleanedFormData = omit(normalizeFormTextFields(formData),
          [
            'requiredAiLearnerGoal',
            'aiCharacterObjections',
            'aiLearnerGoals',
          ]);

        // Filter out empty objections, keeping { id, text } structure for UPDATE
        const aiCharacterObjections = formData.aiCharacterObjections
          .map((item) => ({
            ...item,
            text: normalizeText(item?.text),
          }))
          .filter((item) => item.text !== '');

        // Combine requiredAiLearnerGoal back with aiLearnerGoals, filter empty ones
        const aiLearnerGoals = [
          {
            ...formData.requiredAiLearnerGoal,
            text: normalizeText(formData.requiredAiLearnerGoal?.text),
          },
          ...formData.aiLearnerGoals.map((item) => ({
            ...item,
            text: normalizeText(item?.text),
          })),
        ].filter((item) => item.text !== '');

        return {
          ...cleanedFormData,
          aiCharacterObjections,
          aiLearnerGoals,
        };
      }
      default:
        return {
          ...formData,
        };
    }
  };

  const handleCreateRolePlay = () => {
    const payload = getPayload(getValues(), SubmitType.CREATE);
    setSubmitting(true);
    onSubmit({
      aiRolePlay: {
        ...payload,
      },
    }, false, ComponentType.AI_ROLE_PLAY).then(() => {
      // Show info alert on successful creation if content quality was insufficient
      if (lectureContentQualityStatus === LectureContentQualityStatus.CRITICALLY_INSUFFICIENT
          || lectureContentQualityStatus === LectureContentQualityStatus.INSUFFICIENT) {
        dispatch(addAlertMessage({
          type: AlertMessageType.INFO,
          message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.CONTENT_INSUFFICIENT(),
        }));
      }
    }).catch(() => {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
    }).finally(() => {
      setSubmitting(false);
    });
  };

  const handleUpdateRolePlay = () => {
    const payload = getPayload(getValues(), SubmitType.UPDATE);
    setSubmitting(true);
    onSubmit({
      ...payload,
    }).then((res) => {
      if (onCancel && !res.error) {
        onCancel();
      }
    }).catch(() => {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
    }).finally(() => {
      setSubmitting(false);
    });
  };

  const getSubmitSection = () => (
    <div className='d-flex pt-8 pb-6 justify-content-center btn-container'>
      <Button
        variant='secondary'
        type='button'
        onClick={handleCancel}
        data-qa={config.pendo.activities.aiRolePlay.configModal.cancel}
      >
        {t.FORM.CANCEL()}
      </Button>
      {
        isUpdate ? (
          <Button
            variant='primary'
            type='button'
            disabled={isSubmitDisabled || submitting}
            onClick={handleUpdateRolePlay}
            data-qa={config.pendo.activities.aiRolePlay.configModal.update}
          >
            {submitting ? t.FORM.UPDATING() : t.FORM.UPDATE()}
          </Button>
        ) : (
          <Button
            variant='primary'
            type='button'
            disabled={isSubmitDisabled || submitting}
            onClick={handleCreateRolePlay}
            data-qa={config.pendo.activities.aiRolePlay.configModal.create}
          >
            {submitting ? t.FORM.CREATING() : t.FORM.CREATE()}
          </Button>
        )
      }
    </div>
  );

  const getWarningMessage = () => {
    switch (lectureContentQualityStatus) {
      case LectureContentQualityStatus.CRITICALLY_INSUFFICIENT:
        return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.CONTENT_CRITICALLY_INSUFFICIENT();
      case LectureContentQualityStatus.INSUFFICIENT:
        return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.CONTENT_INSUFFICIENT();
      default:
        return '';
    }
  };

  return (
    <div css={styles}>
      {!!getWarningMessage() && (
        <div className='lecture-content-warning p-4 mt-4'>
          {getWarningMessage()}
        </div>
      )}
      <RolePlayContextSetting />
      <CharacterSetting />
      <LearnerObjectiveSetting />
      {getSubmitSection()}
    </div>
  );
};

export default SetUpInstruction;
